import type { CoachAvailability } from "./types";

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

export const COACH_NAME_MAX = 60;

/**
 * The name a new coach starts with, from their app profile: first and last name
 * when the account has them, because a club-admin login's display name is often
 * the club itself. Then the display name, then the username. The coach can
 * change it in the portal afterwards.
 */
export function coachNameFrom(user: unknown): string | null {
    if (typeof user !== "object" || user === null) return null;
    const u = user as Record<string, unknown>;
    const full = [text(u.firstName), text(u.lastName)].filter(Boolean).join(" ");
    const name = full || text(u.displayName) || text(u.username);
    return name ? name.slice(0, COACH_NAME_MAX) : null;
}

/** "MT" for "Max Thorndon": the avatar when a coach has no photo. Empty for an empty name. */
export function initialsOf(name: string): string {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => (Array.from(w)[0] ?? "").toUpperCase()).join("");
}

/**
 * Whether a coach can take a new request. Paused is the coach's own switch;
 * full means their requests waiting for an answer plus those in progress have
 * reached their limit, and frees up as they return or decline one.
 */
export function coachAvailability(acceptingSubmissions: boolean, openCount: number, maxOpen: number): CoachAvailability {
    if (!acceptingSubmissions) return "paused";
    return openCount >= maxOpen ? "full" : "open";
}

const isAvailability = (v: unknown): v is CoachAvailability => v === "open" || v === "full" || v === "paused";

/** A listing's availability as the server sent it, or read from `available` when an older server sent none. */
export function availabilityOfListing(listing: { available: boolean; availability?: unknown }): CoachAvailability {
    if (isAvailability(listing.availability)) return listing.availability;
    return listing.available ? "open" : "paused";
}
